import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { newAxiosClient } from "@shadowcue/ui";
import type { IUser } from "../../../types/iuser";
import {
  PageContainer,
  PageTopbar,
  PageHeader,
  ContentContainer,
  LoadableCardGrid,
  PreviewCard,
  BreadcrumbItem,
  astryx,
  neutralTheme,
  Search,
  useDocumentTheme,
  defaultGridPagination,
  type IPagination
} from "@shadowcue/ui";
import RenameModal from "./rename_modal";

interface Props {
  currentUser: IUser;
}

interface IConversation {
  id: number;
  name: string;
  description: string | null;
  status: string;
  createdAt: string;
  userId: number;
  userEmail: string;
}

interface IOrgUser {
  id: number;
  email: string;
}

const axios = newAxiosClient();

const SORT_OPTIONS = [
  { value: "newest", label: "Newest" },
  { value: "oldest", label: "Oldest" },
  { value: "name", label: "Name A–Z" },
];

const DEBOUNCE_DURATION = 500;

const Conversations = (props: Props) => {
  const [conversations, setConversations] = useState<IConversation[]>([]);
  const [pagination, setPagination] =
    useState<IPagination>(defaultGridPagination());
  const [loading, setLoading] = useState<boolean>(true);
  const [search, setSearch] = useState<string>("");
  const [debouncedSearch, setDebouncedSearch] = useState<string>("");
  const [sort, setSort] = useState<string>("newest");
  const [dateRange, setDateRange] = useState<astryx.DateRange | null>(null);
  const [owner, setOwner] = useState<string>("mine");
  const [orgUsers, setOrgUsers] = useState<IOrgUser[]>([]);
  const [editingConversation, setEditingConversation] =
    useState<IConversation | null>(null);

  const navigate = useNavigate();
  const mode = useDocumentTheme();
  const isAdmin = props.currentUser.admin;

  const breadcrumbs: BreadcrumbItem[] = [
    { label: "Conversations", href: "/dashboard/conversations" },
  ];

  const fetchConversations = async (page: number = pagination.page) => {
    setLoading(true);
    try {
      const res = await axios.get("/api/conversations", {
        headers: {
          AUTHORIZATION: `Bearer ${props.currentUser.authToken}`,
        },
        params: {
          page,
          limit: pagination.limit,
          search: debouncedSearch || undefined,
          sort,
          createdAfter: dateRange?.start,
          createdBefore: dateRange?.end,
          ...(isAdmin && owner === "all" && { allUsers: true }),
          ...(isAdmin &&
            owner !== "mine" &&
            owner !== "all" && { userId: Number(owner) }),
        },
      });
      setConversations(res.data.conversations || []);
      setPagination(res.data.pagination || defaultGridPagination());
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!isAdmin) return;

    const fetchOrgUsers = async () => {
      try {
        const res = await axios.get("/api/users", {
          headers: {
            AUTHORIZATION: `Bearer ${props.currentUser.authToken}`,
          },
          params: { limit: 100 },
        });
        setOrgUsers(res.data.users || []);
      } catch (error) {
        console.error(error);
      }
    };

    fetchOrgUsers();
  }, []);

  useEffect(() => {
    const timeout = setTimeout(
      () => setDebouncedSearch(search),
      DEBOUNCE_DURATION,
    );
    return () => clearTimeout(timeout);
  }, [search]);

  useEffect(() => {
    fetchConversations(1);
  }, [debouncedSearch, sort, dateRange, owner]);

  const hasConversations = !loading && conversations.length > 0;
  const isNarrowing =
    debouncedSearch.trim() !== "" || dateRange !== null || owner !== "mine";

  const clearFilters = () => {
    setSearch("");
    setSort("newest");
    setDateRange(null);
    setOwner("mine");
  };

  const ownerOptions = [
    { value: "mine", label: "My conversations" },
    { value: "all", label: "All users" },
    ...orgUsers.map((orgUser) => ({
      value: String(orgUser.id),
      label: orgUser.email,
    })),
  ];

  return (
    <astryx.Theme theme={neutralTheme} mode={mode}>
      <PageContainer>
        <PageTopbar breadcrumbs={breadcrumbs} />

        <ContentContainer loading={false}>
          <PageHeader
            title="Conversations"
            description="Launch, manage, and review AI-guided conversations."
          />

          <astryx.VStack gap={6}>
            <astryx.HStack gap={3} align="center" wrap="wrap">
              <astryx.StackItem size="fill">
                <astryx.TextInput
                  label="Search conversations"
                  isLabelHidden
                  startIcon={<astryx.Icon icon={Search} />}
                  placeholder="Search by name…"
                  hasClear
                  width="100%"
                  value={search}
                  onChange={(value: string) => setSearch(value)}
                />
              </astryx.StackItem>

              <astryx.Selector
                label="Sort conversations"
                isLabelHidden
                value={sort}
                options={SORT_OPTIONS}
                onChange={(value: string) => setSort(value)}
              />

              <astryx.DateRangeInput
                label="Filter by date created"
                isLabelHidden
                placeholder="Date created"
                hasClear
                value={dateRange}
                onChange={(value: astryx.DateRange | null) =>
                  setDateRange(value)
                }
              />

              {isAdmin && (
                <astryx.Selector
                  label="Filter by owner"
                  isLabelHidden
                  value={owner}
                  options={ownerOptions}
                  onChange={(value: string) => setOwner(value)}
                />
              )}
            </astryx.HStack>

            <LoadableCardGrid
              loading={loading}
              emptyState={
                isNarrowing
                  ? {
                      headline: "No conversations match",
                      subtext: "Try a different search or clear your filters.",
                      action: (
                        <astryx.Button
                          label="Clear filters"
                          onClick={clearFilters}
                        />
                      ),
                    }
                  : {
                      headline: "No conversations yet",
                      subtext:
                        "Conversations from your calls in the ShadowCue app will appear here.",
                    }
              }
            >
              {conversations.map((conversation) => {
                const showPath = `/dashboard/conversations/${conversation.id}`;
                const showOwnerEmail = isAdmin && owner !== "mine";

                return (
                  <PreviewCard
                    key={conversation.id}
                    title={conversation.name}
                    description={conversation.description}
                    onClick={() => navigate(showPath)}
                    footer={
                      <astryx.Text type="supporting" color="secondary">
                        {new Date(conversation.createdAt).toLocaleDateString()}
                        {showOwnerEmail && ` · ${conversation.userEmail}`}
                      </astryx.Text>
                    }
                    menuItems={[
                      { label: "View", onClick: () => navigate(showPath) },
                      {
                        label: "Edit",
                        onClick: () => setEditingConversation(conversation),
                      },
                    ]}
                  />
                );
              })}
            </LoadableCardGrid>

            {hasConversations && (
              <astryx.HStack justify="end">
                <astryx.Pagination
                  page={pagination.page}
                  totalPages={pagination.pages ?? 1}
                  onChange={(page: number) => fetchConversations(page)}
                  variant="compact"
                />
              </astryx.HStack>
            )}
          </astryx.VStack>
        </ContentContainer>
      </PageContainer>

      <RenameModal
        conversation={editingConversation}
        authToken={props.currentUser.authToken}
        onClose={() => setEditingConversation(null)}
        onSaved={() => fetchConversations(pagination.page)}
      />
    </astryx.Theme>
  );
};

export default Conversations;
